import React, { useState } from 'react';
import { SectionShell, TextAreaField, saveSection } from './shared';

export function ProcurementEditor({ data, dept, deptLabel, onSaved, projectId, date, readOnly }) {
  const [equip, setEquip] = useState(() => (data.equipment_running || []).map(e => ({ ...e })));
  const [notes, setNotes] = useState(data.procurement_notes || '');
  const [pending, setPending] = useState(data.pending_deliveries || '');
  const [saving, setSaving] = useState(false);
  const blank = { name: '', po_no: '', running_hours: 0, idle_hours: 0, hire_cost_hr: 0, remarks: '' };
  const upd = (i, key, val) => setEquip(equip.map((x, idx) => idx === i ? { ...x, [key]: val } : x));

  const save = () => saveSection(projectId, date, dept, {
    equipment_running: equip.filter(e => (e.name || '').trim()).map(e => ({
      name: e.name.trim(), po_no: e.po_no || '',
      running_hours: parseFloat(e.running_hours) || 0,
      idle_hours: parseFloat(e.idle_hours) || 0,
      hire_cost_hr: parseFloat(e.hire_cost_hr) || 0,
      remarks: e.remarks || '',
    })),
    procurement_notes: notes,
    pending_deliveries: pending,
  }, onSaved, deptLabel, setSaving);

  return (
    <SectionShell deptLabel={deptLabel} dept={dept} onSave={save} saving={saving} readOnly={readOnly}>
      <div>
        <div className="flex items-center justify-between mb-1">
          <label className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Equipment on site today</label>
          <button
            onClick={() => setEquip([...equip, { ...blank }])}
            className="text-xs uppercase tracking-wider text-[#3b4872] font-semibold"
            data-testid="dpr-proc-equip-add"
          >
            + Add
          </button>
        </div>
        <div className="space-y-2">
          {equip.length === 0 && (
            <div className="text-[11px] text-slate-400 py-2 text-center border border-dashed border-slate-200 rounded-sm">
              No equipment — click "+ Add"
            </div>
          )}
          {equip.map((e, i) => (
            <div key={i} className="flex flex-wrap items-center gap-2" data-testid={`dpr-proc-equip-row-${i}`}>
              <input value={e.name} onChange={(ev) => upd(i, 'name', ev.target.value)} placeholder="Equipment name" className="flex-1 min-w-[130px] border border-slate-200 rounded-sm text-sm px-2 py-1.5" data-testid={`dpr-proc-equip-name-${i}`} />
              <input value={e.po_no} onChange={(ev) => upd(i, 'po_no', ev.target.value)} placeholder="PO No" className="w-24 border border-slate-200 rounded-sm text-sm px-2 py-1.5" data-testid={`dpr-proc-equip-po-${i}`} />
              <input type="number" step="any" value={e.running_hours} onChange={(ev) => upd(i, 'running_hours', ev.target.value)} placeholder="Run hrs" className="w-20 border border-slate-200 rounded-sm text-sm px-2 py-1.5 text-right tabular-nums" data-testid={`dpr-proc-equip-run-${i}`} />
              <input type="number" step="any" value={e.idle_hours} onChange={(ev) => upd(i, 'idle_hours', ev.target.value)} placeholder="Idle hrs" className="w-20 border border-slate-200 rounded-sm text-sm px-2 py-1.5 text-right tabular-nums" data-testid={`dpr-proc-equip-idle-${i}`} />
              <input type="number" step="any" value={e.hire_cost_hr ?? 0} onChange={(ev) => upd(i, 'hire_cost_hr', ev.target.value)} placeholder="₹/hr" className="w-20 border border-slate-200 rounded-sm text-sm px-2 py-1.5 text-right tabular-nums" data-testid={`dpr-proc-equip-hire-${i}`} title="Hire cost per hour (₹)" />
              <input value={e.remarks} onChange={(ev) => upd(i, 'remarks', ev.target.value)} placeholder="Remarks" className="flex-1 min-w-[120px] border border-slate-200 rounded-sm text-sm px-2 py-1.5" data-testid={`dpr-proc-equip-remarks-${i}`} />
              <button onClick={() => setEquip(equip.filter((_, idx) => idx !== i))} className="text-rose-600 text-sm">×</button>
            </div>
          ))}
        </div>
      </div>
      <TextAreaField label="Procurement notes" value={notes} onChange={setNotes} rows={2} testid="dpr-proc-notes" />
      <TextAreaField label="Expected deliveries / pending items" value={pending} onChange={setPending} rows={2} testid="dpr-proc-pending" />
    </SectionShell>
  );
}
